import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarDia, formatarInteiro, formatarReais } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { api, type DepreciacaoDaMaquina } from './api';
import {
  corpoDaDepreciacao,
  efeitoDaDepreciacao,
  faltaNaDepreciacao,
  parcelaDoFormulario,
  parcelaEscrita,
  valoresIniciaisDaDepreciacao,
  vidaUtilEscrita,
  type ValoresDaDepreciacao,
} from './margem';

/**
 * A DEPRECIAÇÃO DE CADA MÁQUINA (0096) na tela de custos: todas as máquinas
 * ativas, inclusive as sem dado — a máquina sem dado é a lacuna que deixa a
 * margem parcial, e escondê-la seria esconder por quê.
 *
 * Linear por mês: (aquisição − residual) ÷ vida útil, desde o início da
 * depreciação. Gravar é por vigência e com motivo, como o valor-hora.
 */
export function DepreciacaoDasMaquinas({ podeGravar }: { readonly podeGravar: boolean }) {
  const base = useId();
  const consulta = useQuery({ queryKey: ['custos', 'depreciacao'], queryFn: api.depreciacao });
  const maquinas = consulta.data?.maquinas ?? [];
  const semDado = maquinas.filter((m) => m.vigente === null).length;
  const [definindo, setDefinindo] = useState<DepreciacaoDaMaquina | null>(null);
  /* O atalho para cadastrar máquina só para quem cadastra (`equipamento.gerir`). */
  const sessao = useSessao();
  const podeCadastrarMaquina = (sessao.data?.permissoes ?? []).includes('equipamento.gerir');

  const COLUNAS: readonly Coluna<DepreciacaoDaMaquina>[] = [
    {
      chave: 'maquina',
      rotulo: 'Máquina',
      celula: (m) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{m.codigo}</span>
          <TextoDaCelula texto={m.descricao} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'aquisicao',
      rotulo: 'Aquisição e residual',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (m) =>
        m.vigente ? (
          <span className="flex flex-col items-end">
            <span className="numero whitespace-nowrap text-tinta">{formatarReais(m.vigente.valorAquisicao)}</span>
            <span className="txt-apoio whitespace-nowrap text-tinta-apagada">residual {formatarReais(m.vigente.valorResidual)}</span>
          </span>
        ) : (
          <span className="text-tinta-secundaria">—</span>
        ),
    },
    {
      chave: 'vida',
      rotulo: 'Vida útil',
      prioridade: 'secundaria',
      celula: (m) =>
        m.vigente ? (
          <span className="flex min-w-0 flex-col">
            <span className="txt-corpo text-tinta">{vidaUtilEscrita(m.vigente.vidaUtilMeses)}</span>
            <span className="txt-apoio text-tinta-apagada">desde {formatarDia(m.vigente.inicio)}</span>
          </span>
        ) : (
          <span className="text-tinta-secundaria">—</span>
        ),
    },
    {
      chave: 'parcela',
      rotulo: 'Por mês',
      alinhamento: 'numero',
      celula: (m) =>
        m.vigente === null ? (
          /* A lacuna em selo preenchido (âmbar, atenção): a palavra vem junto da cor. */
          <span className="inline-flex h-[var(--selo-altura)] items-center gap-1.5 whitespace-nowrap rounded-pill bg-atencao-fundo px-2.5 text-atencao-texto">
            <span aria-hidden="true" className="size-[var(--selo-ponto)] rounded-pill bg-atencao" />
            <span className="txt-selo">Sem dado</span>
          </span>
        ) : (
          <span className="numero whitespace-nowrap text-tinta">{parcelaEscrita(m.vigente.parcelaMensal)}</span>
        ),
    },
  ];

  return (
    <section className="cartao flex flex-col gap-4 p-6" aria-labelledby={`${base}-titulo`}>
      <div className="flex min-w-0 max-w-[80ch] flex-col gap-1">
        <h2 id={`${base}-titulo`} className="txt-secao text-tinta">
          Depreciação das máquinas
        </h2>
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          Linear por mês: o valor de aquisição menos o residual, dividido pela vida útil, desde o início da depreciação. Entra no
          custo total e na margem. Máquina sem dado não deprecia zero: fica fora, e o total diz qual.
          {semDado > 0 ? ` ${semDado === 1 ? '1 máquina ativa está' : `${formatarInteiro(semDado)} máquinas ativas estão`} sem dado.` : ''}
        </p>
      </div>

      <Tabela
        rotulo="Depreciação das máquinas"
        colunas={COLUNAS}
        linhas={maquinas}
        chaveDaLinha={(m) => m.equipamentoId}
        carregando={consulta.isPending}
        alturaDupla
        /* Sem `depreciacao.gravar`, a linha não oferece definir (prova do
           marco, 07/10/2026): antes o item aparecia desligado no menu. */
        {...(podeGravar
          ? {
              acoes: (m: DepreciacaoDaMaquina) => (
                <MaisAcoes
                  acoes={[
                    {
                      rotulo: m.vigente ? 'Corrigir depreciação' : 'Definir depreciação',
                      aoEscolher: () => {
                        setDefinindo(m);
                      },
                    },
                  ]}
                />
              ),
            }
          : {})}
        erro={consulta.isError ? <ErroDaLista oQue="A depreciação das máquinas" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={
          <EstadoVazio
            titulo="Nenhuma máquina ativa"
            descricao="A depreciação é por máquina. Cadastre as máquinas da frota e volte aqui para dar o valor de aquisição e a vida útil."
            acao={
              podeCadastrarMaquina ? (
                <Link to="/equipamentos/novo" className="botao botao-secundario">
                  Cadastrar equipamento
                </Link>
              ) : undefined
            }
          />
        }
      />

      <JanelaDaDepreciacao
        maquina={podeGravar ? definindo : null}
        aoFechar={() => {
          setDefinindo(null);
        }}
      />
    </section>
  );
}

/**
 * A janela de definir a depreciação de uma máquina — a mesma na tela de custos
 * e na ficha da máquina. Seis campos (p.30): vigência, início, aquisição,
 * residual, vida útil e motivo.
 */
export function JanelaDaDepreciacao({ maquina, aoFechar }: { readonly maquina: DepreciacaoDaMaquina | null; readonly aoFechar: () => void }) {
  const base = useId();
  const cliente = useQueryClient();
  const [valores, setValores] = useState<ValoresDaDepreciacao>(() => valoresIniciaisDaDepreciacao(maquina ?? undefined, new Date()));
  const [vista, setVista] = useState<string | null>(null);
  const [tentou, setTentou] = useState(false);
  /* Ao abrir para outra máquina, o formulário parte do que ela tem hoje. */
  const chave = maquina?.equipamentoId ?? null;
  if (chave !== vista) {
    setVista(chave);
    if (maquina) {
      setValores(valoresIniciaisDaDepreciacao(maquina, new Date()));
      setTentou(false);
    }
  }
  const falta = faltaNaDepreciacao(valores);
  const parcela = parcelaDoFormulario(valores);

  const definir = useMutation({
    mutationFn: (v: ValoresDaDepreciacao) => api.definirDepreciacao(maquina?.equipamentoId ?? '', corpoDaDepreciacao(v)),
    meta: {
      retorno: retornoDe<{ parcelaMensal: number; desde: string }, ValoresDaDepreciacao>((r, v) => ({
        titulo: 'Depreciação da máquina definida',
        ...(maquina ? { alvo: { codigo: maquina.codigo, nome: maquina.descricao } } : {}),
        fatos: [parcelaEscrita(r.parcelaMensal), `vida útil ${vidaUtilEscrita(Number(v.vida))}`, `motivo: ${v.motivo.trim()}`],
        efeito: efeitoDaDepreciacao(maquina ?? undefined, r.desde, r.parcelaMensal),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['custos'] });
      void cliente.invalidateQueries({ queryKey: ['equipamentos'] });
      aoFechar();
    },
  });

  return (
    <Janela
      titulo={maquina ? `Depreciação da ${maquina.codigo}` : 'Depreciação da máquina'}
      descricao="Vale a partir da data, até a próxima vigência. Na mesma data, corrige os valores."
      aberta={maquina !== null}
      aoFechar={() => {
        definir.reset();
        aoFechar();
      }}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            nivel="primario"
            disabled={definir.isPending}
            onClick={() => {
              setTentou(true);
              if (Object.keys(falta).length > 0) return;
              definir.mutate(valores);
            }}
          >
            {definir.isPending ? 'Gravando…' : 'Gravar depreciação'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {definir.isError ? (
          <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
            <span className="txt-corpo text-tinta">{explicarFalha(definir.error).erro}</span>
            <span className="txt-apoio text-tinta-secundaria">{explicarFalha(definir.error).comoResolver}</span>
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo id={`${base}-inicio`} rotulo="Início da depreciação" erro={tentou ? (falta.inicio ?? null) : null} ajuda="O dia em que a máquina entrou em uso.">
            <input
              id={`${base}-inicio`}
              type="date"
              className="controle"
              value={valores.inicio}
              onChange={(e) => {
                setValores({ ...valores, inicio: e.target.value });
              }}
            />
          </Campo>
          <Campo id={`${base}-desde`} rotulo="Vale a partir de" erro={tentou ? (falta.desde ?? null) : null} ajuda="Sugerido: o 1º dia do mês anterior.">
            <input
              id={`${base}-desde`}
              type="date"
              className="controle"
              value={valores.desde}
              onChange={(e) => {
                setValores({ ...valores, desde: e.target.value });
              }}
            />
          </Campo>
          <CampoTexto
            rotulo="Valor de aquisição (R$)"
            valor={valores.aquisicao}
            aoMudar={(aquisicao) => {
              setValores({ ...valores, aquisicao });
            }}
            placeholder="1.300.000,00"
            validar={(texto) => faltaNaDepreciacao({ ...valores, aquisicao: texto }).aquisicao ?? null}
          />
          <CampoTexto
            rotulo="Valor residual (R$)"
            valor={valores.residual}
            aoMudar={(residual) => {
              setValores({ ...valores, residual });
            }}
            placeholder="100.000,00"
            ajuda="O que a máquina vale no fim da vida útil. Sem valor, 0."
            validar={(texto) => faltaNaDepreciacao({ ...valores, residual: texto }).residual ?? null}
          />
          <CampoTexto
            rotulo="Vida útil (meses)"
            valor={valores.vida}
            aoMudar={(vida) => {
              setValores({ ...valores, vida });
            }}
            placeholder="120"
            ajuda={parcela === null ? 'Em meses: 10 anos são 120.' : `Deprecia ${parcelaEscrita(parcela)}.`}
            validar={(texto) => faltaNaDepreciacao({ ...valores, vida: texto }).vida ?? null}
          />
        </div>
        <CampoDoMotivo
          rotulo="De onde vêm os dados"
          valor={valores.motivo}
          aoMudar={(motivo) => {
            setValores({ ...valores, motivo });
          }}
          exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['depreciacao.definir']}`}
        />
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          {`Muda o custo total e a margem de todo período consultado que cair na vigência, inclusive meses já vistos.${
            maquina?.vigente ? ` Hoje: ${parcelaEscrita(maquina.vigente.parcelaMensal)}.` : ''
          }`}
        </p>
      </div>
    </Janela>
  );
}
